import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { apiUrl } from '../http/api-url';

export interface AuthTokenResponse {
  successful: boolean;
  token: string;
}

class FormEncodingCodec implements HttpParameterCodec {
  encodeKey = (key: string) => encodeURIComponent(key);
  encodeValue = (value: string) => encodeURIComponent(value);
  decodeKey = (key: string) => decodeURIComponent(key);
  decodeValue = (value: string) => decodeURIComponent(value);
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  authenticate(username: string, password: string): Observable<AuthTokenResponse> {
    const body = new HttpParams({ encoder: new FormEncodingCodec() })
      .set('username', username)
      .set('password', password);
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post<AuthTokenResponse>(apiUrl('/auth/login'), body, { headers });
  }

  verifyAccessToken(otp: string, token: string): Observable<AuthTokenResponse> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.put<AuthTokenResponse>(apiUrl(`/auth/access_token/verify/${otp}`), null, { headers });
  }

  refreshAccessToken(refreshToken: string): Observable<AuthTokenResponse> {
    return this.http.post<AuthTokenResponse>(apiUrl(`/auth/access_token/refresh/${refreshToken}`), null);
  }

  exchangeAccessToken(customerUid: string): Observable<AuthTokenResponse> {
    return this.http.post<AuthTokenResponse>(apiUrl(`/auth/access_token/exchange/${customerUid}`), null);
  }

  invalidateAccessToken(): Observable<unknown> {
    return this.http.put(apiUrl('/auth/access_token/invalidate'), null);
  }

  verifySharedSession(secureKey: string): Observable<boolean> {
    const headers = new HttpHeaders().set('Accept', '*/*');
    return this.http
      .post(apiUrl(`/auth/access_token/shared-session/${secureKey}`), null, { headers, observe: 'response' })
      .pipe(map(response => response.headers.has('Authorization')));
  }
}
